import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { IconButton } from 'shared/ui/IconButton'
import { getAriaLabel } from 'shared/lib'
import { paginationCountList as paginationCountListConst } from 'shared/ui/Table/constants/constants'
import { PaginationSelect } from './PaginationSelect'
import styles from './styles.module.scss'

interface IPaginationProps {
  rowsCount?: number
  hasMore?: boolean
  itemsCount?: number
  onChange: (page: number, pageSize: number) => void
  initPage?: number
  limit?: number | null
  startSize?: number | null
}

export const Pagination = observer(
  ({
    rowsCount,
    hasMore,
    itemsCount = 0,
    onChange,
    initPage,
    limit,
    startSize,
  }: IPaginationProps) => {
    const paginationCountList = startSize
      ? paginationCountListConst.filter((count) => count >= startSize)
      : paginationCountListConst

    const [pageSize, setPageSize] = useState<number>(limit || paginationCountList[0])
    const [page, setPage] = useState<number>(initPage || 1)
    const hasKnownTotal = rowsCount !== undefined
    const pageCount = hasKnownTotal ? Math.ceil(rowsCount / pageSize) : null
    const from = `${(page - 1) * pageSize + 1} `
    const maxCount = page * pageSize
    const to = hasKnownTotal
      ? maxCount > rowsCount
        ? rowsCount
        : maxCount
      : (page - 1) * pageSize + itemsCount
    const fromToValue = `${from} - ${to}`
    const singlePageTable = hasKnownTotal ? pageCount === 1 : page === 1 && !hasMore
    const isNextDisabled = hasKnownTotal
      ? singlePageTable || page >= (pageCount as number)
      : !hasMore

    const changePageSize = (newSize: number) => {
      if (newSize === pageSize) return

      setPageSize(newSize)
      setPage(1)
      onChange(1, newSize)
    }

    const changePage = (newPage: number) => {
      onChange(newPage, pageSize)
      setPage(newPage)
    }

    useEffect(() => {
      setPage(initPage || 1)
    }, [initPage])

    useEffect(() => {
      setPageSize(limit || paginationCountList[0])
    }, [limit])

    return (
      <div className={styles.container} aria-label={getAriaLabel('Pagination')}>
        <IconButton
          icon={'chevronLeft'}
          size={'small'}
          variant={'icon'}
          color={'secondary'}
          onClick={() => {
            changePage(page - 1)
          }}
          disabled={page === 1}
          ariaLabel='Pagination_previous'
        />

        <IconButton
          icon={'chevronRight'}
          size={'small'}
          variant={'icon'}
          color={'secondary'}
          onClick={() => {
            changePage(page + 1)
          }}
          disabled={isNextDisabled}
          ariaLabel='Pagination_next'
        />

        <PaginationSelect
          fromToValue={fromToValue}
          pageSize={pageSize}
          setPageSize={changePageSize}
          paginationCountList={paginationCountList}
        />

        {hasKnownTotal ? (
          <span className={styles.allRowsCount}>of {rowsCount}</span>
        ) : (
          <span className={styles.allRowsCount}>Page {page}</span>
        )}
      </div>
    )
  }
)
